import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  // Alert,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import { UserDTO, CreatePaymentRequest } from '../types';
import adminService from '../services/adminService';
import { formatPhone } from '../utils/phoneFormatter';
import { rublesToKopecks } from '../utils/amountFormatter';

interface CreatePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  users: UserDTO[];
  onSuccess: () => void;
  selectedUserId?: string; // Для выплаты одному пользователю
}

const CreatePaymentDialog: React.FC<CreatePaymentDialogProps> = ({
  open,
  onClose,
  users,
  onSuccess,
  selectedUserId,
}) => {
  const [formData, setFormData] = useState({
    userIds: selectedUserId ? [selectedUserId] : [],
    amount: '',
    reason: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedUserId) {
      setFormData(prev => ({ ...prev, userIds: [selectedUserId] }));
    }
  }, [selectedUserId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (formData.userIds.length === 0) {
      newErrors.userIds = 'Выберите хотя бы одного получателя';
    }

    if (!formData.amount) {
      newErrors.amount = 'Сумма обязательна';
    } else if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      newErrors.amount = 'Введите корректную сумму';
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Основание выплаты обязательно';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const paymentData: CreatePaymentRequest = {
        userIds: formData.userIds,
        amount: rublesToKopecks(Number(formData.amount)), // Конвертируем рубли в копейки
        reason: formData.reason.trim(),
      };

      await adminService.createPayment(paymentData);
      onSuccess();
    } catch (error) {
      console.error('Ошибка создания выплаты:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      userIds: selectedUserId ? [selectedUserId] : [],
      amount: '',
      reason: '',
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  const handleUserSelection = (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      userIds: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const getSelectedUsers = () => {
    return users.filter(user => formData.userIds.includes(user.id));
  };

  const calculateTotalAmount = () => {
    return Number(formData.amount || 0) * formData.userIds.length;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {selectedUserId ? 'Создать выплату' : 'Создать массовую выплату'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Выбор пользователей */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.userIds}>
                <InputLabel>Получатели</InputLabel>
                <Select
                  multiple
                  value={formData.userIds}
                  onChange={handleUserSelection}
                  input={<OutlinedInput label="Получатели" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const user = users.find(u => u.id === value);
                        return (
                          <Chip
                            key={value}
                            label={user?.personalData?.fullName || 'Не указано'}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  disabled={!!selectedUserId}
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      <Checkbox checked={formData.userIds.indexOf(user.id) > -1} />
                      <ListItemText
                        primary={user.personalData?.fullName || 'Не указано'}
                        secondary={`ИНН: ${user.personalData?.inn || 'Не указано'}, ${formatPhone(String(user.phone))}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
                {errors.userIds && <FormHelperText>{errors.userIds}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Сумма */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Сумма выплаты (₽)"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                type="number"
                error={!!errors.amount}
                helperText={errors.amount || "Введите сумму в рублях (например: 1000.50)"}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Общая сумма */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Общая сумма:
                </Typography>
                <Typography variant="h6" color="primary">
                  {new Intl.NumberFormat('ru-RU', {
                    style: 'currency',
                    currency: 'RUB',
                  }).format(calculateTotalAmount())}
                </Typography>
              </Box>
            </Grid>

            {/* Основание выплаты */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Основание выплаты"
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                multiline
                rows={3}
                error={!!errors.reason}
                helperText={errors.reason}
                placeholder="Укажите основание для выплаты..."
              />
            </Grid>

            {/* Список выбранных пользователей */}
            {formData.userIds.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Выбранные получатели ({formData.userIds.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {getSelectedUsers().map((user) => (
                    <Chip
                      key={user.id}
                      label={user.personalData?.fullName || 'Не указано'}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || formData.userIds.length === 0}
          >
            {isSubmitting ? 'Создание...' : 'Создать выплату'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreatePaymentDialog;
